import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { Search, Plus } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import PagyPagination from '@/components/PagyPagination'
import { useOrgPath } from '@/lib/pathFor'

interface CarrierItem {
  id: string
  name: string
  code: string | null
  active_bookings_count: number
  api_status: string
  show_url: string
}

interface CarriersIndexProps {
  carriers: CarrierItem[]
  search: string
  pagy: { page: number; pages: number; count: number; next: number | null; prev: number | null }
}

export default function CarriersIndex({ carriers, search: initialSearch, pagy }: CarriersIndexProps) {
  const [search, setSearch] = useState(initialSearch)
  const pathFor = useOrgPath()

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(pathFor('/settings/carriers'), { search }, { preserveState: true })
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Carriers</h1>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search carriers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-[200px] pl-8 text-[13px]"
              />
            </div>
          </form>
          <Button
            size="sm"
            className="text-xs"
            onClick={() => router.visit(pathFor('/settings/carriers/new'))}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            New Carrier
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground w-[220px]">Carrier</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground w-[140px]">Active Bookings</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">API Status</th>
            </tr>
          </thead>
          <tbody>
            {carriers.map((carrier) => (
              <tr
                key={carrier.id}
                className="border-b hover:bg-muted/30 cursor-pointer"
                onClick={() => router.visit(carrier.show_url)}
              >
                <td className="px-6 py-3">
                  <div className="flex flex-col">
                    <Link href={carrier.show_url} className="text-[13px] font-medium text-foreground hover:underline">
                      {carrier.name}
                    </Link>
                    {carrier.code && <span className="text-[12px] text-muted-foreground font-mono">{carrier.code}</span>}
                  </div>
                </td>
                <td className="px-6 py-3 text-[13px] text-foreground">{carrier.active_bookings_count}</td>
                <td className="px-6 py-3">
                  <Badge
                    variant="secondary"
                    size="lg"
                    className={`font-medium rounded-full ${
                      carrier.api_status === 'connected'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {carrier.api_status === 'connected' ? 'Connected' : 'Not Set'}
                  </Badge>
                </td>
              </tr>
            ))}
            {carriers.length === 0 && (
              <tr><td colSpan={3} className="px-6 py-8 text-center text-sm text-muted-foreground">No carriers found</td></tr>
            )}
          </tbody>
        </table>
        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

CarriersIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
